/**
 * CurrencyDisplay Component
 * Format and display currency amounts with proper locale formatting
 */

import type { CurrencyCode } from '../../utils/formatting';

export interface CurrencyDisplayProps {
  amount: number;
  currency?: CurrencyCode;
  locale?: string;
  showSymbol?: boolean;
  showCode?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  color?: 'default' | 'success' | 'danger' | 'muted';
}

export function CurrencyDisplay({
  amount,
  currency = 'MYR',
  locale = 'en-MY',
  showSymbol = true,
  showCode = false,
  className = '',
  size = 'md',
  fontWeight = 'normal',
  color = 'default',
}: CurrencyDisplayProps) {
  // Currency symbols mapping
  const currencySymbols: Record<CurrencyCode, string> = {
    MYR: 'RM',
    USD: '$',
    EUR: '€',
    SGD: 'S$',
    AED: 'AED',
    GBP: '£',
    SAR: 'SAR',
    IDR: 'Rp',
  };

  // Format number with locale
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const sizeStyles = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-2xl',
  };

  const weightStyles = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  const colorStyles = {
    default: 'text-gray-900',
    success: 'text-green-600',
    danger: 'text-red-600',
    muted: 'text-gray-500',
  };

  const formattedAmount = formatNumber(amount);
  const symbol = currencySymbols[currency];

  return (
    <span
      className={`
        ${sizeStyles[size]}
        ${weightStyles[fontWeight]}
        ${colorStyles[color]}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {showSymbol && <span className="mr-1">{symbol}</span>}
      <span>{formattedAmount}</span>
      {showCode && <span className="ml-1 text-xs opacity-75">{currency}</span>}
    </span>
  );
}

/**
 * CurrencyCompare Component
 * Display two currency amounts for comparison (e.g., before/after conversion)
 */
export interface CurrencyCompareProps {
  fromAmount: number;
  fromCurrency: CurrencyCode;
  toAmount: number;
  toCurrency: CurrencyCode;
  showRate?: boolean;
  className?: string;
}

export function CurrencyCompare({
  fromAmount,
  fromCurrency,
  toAmount,
  toCurrency,
  showRate = false,
  className = '',
}: CurrencyCompareProps) {
  const rate = toAmount / fromAmount;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <CurrencyDisplay
        amount={fromAmount}
        currency={fromCurrency}
        size="sm"
        color="muted"
      />
      <svg
        className="h-4 w-4 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 7l5 5m0 0l-5 5m5-5H6"
        />
      </svg>
      <CurrencyDisplay
        amount={toAmount}
        currency={toCurrency}
        size="md"
        fontWeight="semibold"
      />
      {showRate && (
        <span className="text-xs text-gray-500 ml-2">
          (Rate: {rate.toFixed(4)})
        </span>
      )}
    </div>
  );
}
